import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Play, LogIn } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/demo-login`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      localStorage.setItem('erp_token', data.access_token);
      localStorage.setItem('erp_user', JSON.stringify({ ...data.user, is_demo: true }));
      localStorage.setItem('erp_demo_mode', 'true');
      window.location.href = '/dashboard';
    } catch { alert('Demo unavailable. Please try again.'); }
    finally { setDemoLoading(false); }
  };

  const NAV = [
    { label: 'Home', path: '/' },
    { label: 'Features', path: '/features' },
    { label: 'Security', path: '/security' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0c1629]/95 backdrop-blur-xl border-b border-white/5 shadow-lg' : 'bg-[#0c1629] backdrop-blur-md'}`} data-testid="marketing-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-18 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0" data-testid="marketing-logo">
          <img
            src="/logos/bizcore-logo.png"
            alt="BizCore - Your Core Business & Compliance Partner"
            className="h-12 sm:h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map(n => (
            <Link key={n.path} to={n.path}
              className={`px-4 py-2 rounded-lg text-base font-medium transition-colors duration-200 ${isActive(n.path) ? 'text-[#d4a84b] font-semibold' : 'text-zinc-300 hover:text-[#d4a84b] hover:bg-white/5'}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-2">
          <Link to="/login" className="flex items-center gap-1.5 text-base font-semibold text-zinc-300 hover:text-[#d4a84b] px-4 py-2 rounded-lg hover:bg-white/5 transition-colors duration-200" data-testid="nav-signin">
            <LogIn size={16} /> Sign In
          </Link>
          <button onClick={handleDemo} disabled={demoLoading}
            className="bg-[#d4a84b] text-[#0c1629] hover:bg-[#e8bc5e] rounded-lg font-semibold px-5 py-2.5 text-sm transition-all flex items-center gap-2 shadow-md hover:shadow-lg" data-testid="nav-demo-btn">
            <Play size={14} fill="currentColor" />{demoLoading ? 'Loading...' : 'Try Demo'}
          </button>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden p-2 rounded-lg text-zinc-300 hover:text-[#d4a84b] hover:bg-white/5" data-testid="mobile-menu-btn">
          {mobileMenu ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenu && (
        <div className="lg:hidden bg-[#0c1629]/98 backdrop-blur-xl border-t border-white/5 py-4 px-6 space-y-1 animate-in slide-in-from-top-2">
          {NAV.map(n => (
            <Link key={n.path} to={n.path} onClick={() => setMobileMenu(false)}
              className={`block px-4 py-2.5 rounded-lg text-base font-medium transition-colors duration-200 ${isActive(n.path) ? 'text-[#d4a84b] font-semibold' : 'text-zinc-300 hover:text-[#d4a84b]'}`}>
              {n.label}
            </Link>
          ))}
          <hr className="border-white/10 my-2" />
          <Link to="/login" onClick={() => setMobileMenu(false)} className="block px-4 py-2.5 text-base font-semibold text-zinc-300 hover:text-[#d4a84b]">Sign In</Link>
          <button onClick={handleDemo} disabled={demoLoading} className="w-full bg-[#d4a84b] text-[#0c1629] rounded-lg font-semibold px-5 py-2.5 text-sm flex items-center justify-center gap-2 mt-2">
            <Play size={14} fill="currentColor" />{demoLoading ? 'Loading...' : 'Try Demo'}
          </button>
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="bg-[#0A0A0A] text-white pt-16 pb-8" data-testid="marketing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Column with Logo */}
          <div>
            <div className="mb-5">
              <img
                src="/logos/bizcore-logo.png"
                alt="BizCore - Your Core Business & Compliance Partner"
                className="h-12 w-auto object-contain"
                style={{ mixBlendMode: 'screen' }}
              />
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed mb-3">
              Your Core Business & Compliance Partner. Built for SMEs, Accountants & Auditors managing multiple companies.
            </p>
            <p className="text-xs text-zinc-500">SGK Apps, Dubai, UAE</p>
            <a href="https://bizcore.sgkapps.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#d4a84b] hover:text-[#e8bc5e] transition-colors">bizcore.sgkapps.com</a>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Product</h4>
            <ul className="space-y-2.5">
              {[{ label: 'Features', to: '/features' }, { label: 'Security', to: '/security' }, { label: 'Pricing', to: '/pricing' }, { label: 'Training Manual', to: '/features#manual' }].map(l => (
                <li key={l.to}><Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/contact" className="text-sm text-zinc-400 hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/terms" className="text-sm text-zinc-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="text-sm text-zinc-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/disclaimer" className="text-sm text-zinc-400 hover:text-white transition-colors">Disclaimer</Link></li>
            </ul>
          </div>

          {/* Editions & Target Audience */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Editions</h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src="/logos/bizcore-logo.png" alt="BizCore" className="h-7 w-auto object-contain rounded" />
                <span className="text-xs text-zinc-400">For SMEs</span>
              </div>
              <div className="flex items-center gap-3">
                <img src="/logos/bizcore-pro-logo.png" alt="BizCore Pro" className="h-7 w-auto object-contain rounded" />
                <span className="text-xs text-zinc-400">For Accountants & Auditors</span>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-zinc-800">
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider font-semibold mb-2">Suitable For</p>
              <p className="text-sm text-zinc-400 leading-relaxed">Accountants &middot; Auditors &middot; SMEs &middot; Multi-Company Firms</p>
            </div>
            <div className="mt-3">
              <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition-colors">Sign In</Link>
              <span className="text-zinc-700 mx-2">|</span>
              <Link to="/register" className="text-sm text-zinc-400 hover:text-white transition-colors">Register</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-800 pt-6 flex flex-col items-center gap-3">
          <p className="text-xs text-zinc-500 text-center">&copy; {new Date().getFullYear()} SGK Apps, Dubai, UAE. All rights reserved. BizCore&trade; & BizCore Pro&trade; are trademarks of SGK Apps.</p>
          <p className="text-xs text-zinc-500 text-center">SGK Apps, a <span className="text-zinc-400">VRV Technical & Technology Solutions FZ LLC</span>.</p>
          <p className="text-xs text-zinc-600">Empowering businesses across the Middle East</p>
        </div>
      </div>
    </footer>
  );
}
